import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Users, Plus, Search, Building2, Phone, Mail, KeyRound, Power, Trash2, Shield,
  Activity, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle, ShieldAlert,
} from "lucide-react";

export default function FactoryAdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [openCreate, setOpenCreate] = useState(false);
  const [openReset, setOpenReset] = useState(null); // admin obj
  const [openPerms, setOpenPerms] = useState(null); // admin obj
  const [expanded, setExpanded] = useState({});
  const [auditRows, setAuditRows] = useState({});

  const loadRefs = async () => {
    try { const r = await api.get("/branches"); setBranches(r.data); } catch { /* */ }
  };
  useEffect(() => { loadRefs(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (branchFilter) params.set("branch_id", branchFilter);
      if (activeFilter) params.set("active", activeFilter === "active" ? "true" : "false");
      const q = params.toString() ? `?${params.toString()}` : "";
      const r = await api.get(`/factory-admins${q}`);
      setAdmins(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter, activeFilter]);

  const doSearch = (e) => { e?.preventDefault?.(); load(); };

  const toggleActive = async (a) => {
    try {
      const { data } = await api.post(`/factory-admins/${a.id}/toggle-active`);
      toast.success(data.is_active ? "Enabled" : "Disabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ${a.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/factory-admins/${a.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reassign = async (a, newBranchId) => {
    try {
      await api.patch(`/factory-admins/${a.id}`, { branch_id: newBranchId });
      toast.success("Reassigned"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleExpanded = async (aid) => {
    setExpanded((e) => ({ ...e, [aid]: !e[aid] }));
    if (!auditRows[aid]) {
      try {
        const r = await api.get(`/factory-admins/${aid}/audit?limit=10`);
        setAuditRows((rows) => ({ ...rows, [aid]: r.data }));
      } catch { /* */ }
    }
  };

  return (
    <div className="space-y-6" data-testid="factory-admins-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Super Admin · Access Management</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Factory Admin Management</h1>
          <p className="text-sm text-[#8B7355] mt-1">Dedicated console for factory admins — assign factories, reset credentials, fine-tune per-user permissions, and review recent activity.</p>
        </div>
        <Button onClick={() => setOpenCreate(true)} className="bg-[#C05621] text-white" data-testid="factory-admin-new">
          <Plus className="w-4 h-4 mr-1" /> Add Factory Admin
        </Button>
      </div>

      {/* Filters */}
      <form onSubmit={doSearch} className="flex flex-wrap gap-2 items-end p-3 bg-white border border-[#EAE5D9] rounded-md">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase text-[#8B7355]">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email or phone…" className="pl-7" data-testid="factory-admin-search" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Factory</Label>
          <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="factory-admin-branch-filter"><SelectValue placeholder="All factories" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All factories</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Status</Label>
          <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-36" data-testid="factory-admin-active-filter"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">Any</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Disabled only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" variant="outline" size="sm"><Search className="w-3.5 h-3.5 mr-1" /> Search</Button>
        <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </form>

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="factory-admins-grid">
        {admins.map((a) => (
          <div key={a.id} className={`bg-white border ${a.is_active === false ? "border-red-200" : "border-[#EAE5D9]"} rounded-lg p-4`} data-testid={`factory-admin-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-[Outfit] text-lg font-semibold truncate">{a.name}</span>
                  {a.is_active === false ? (
                    <Badge className="border-0 bg-red-100 text-red-800 text-[10px]"><XCircle className="w-3 h-3 mr-0.5" /> Disabled</Badge>
                  ) : (
                    <Badge className="border-0 bg-emerald-100 text-emerald-800 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Active</Badge>
                  )}
                </div>
                <div className="text-xs text-[#5C4033] flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /> {a.email}</div>
                {a.phone && <div className="text-xs text-[#5C4033] flex items-center gap-1.5"><Phone className="w-3 h-3" /> {a.phone}</div>}
                <div className="text-xs mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <Building2 className="w-3.5 h-3.5 text-[#C05621]" />
                  <b>{a.branch?.name || "Unassigned"}</b>
                  {a.branch?.city && <span className="text-[#8B7355]">· {a.branch.city}{a.branch?.state ? `, ${a.branch.state}` : ""}</span>}
                  {a.branch?.code && <Badge variant="outline" className="text-[10px] py-0">{a.branch.code}</Badge>}
                </div>
                {a.last_activity_at && (
                  <div className="text-[10px] text-[#8B7355] mt-1 flex items-center gap-1"><Activity className="w-3 h-3" /> Last activity: <span className="font-medium">{a.last_activity_action}</span> · {new Date(a.last_activity_at).toLocaleString()}</div>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setOpenPerms(a)} data-testid={`fa-perms-${a.id}`}><Shield className="w-3 h-3 mr-1" /> Permissions</Button>
                <Button size="sm" variant="outline" onClick={() => setOpenReset(a)} data-testid={`fa-reset-${a.id}`}><KeyRound className="w-3 h-3 mr-1" /> Reset PW</Button>
              </div>
            </div>

            {/* Inline actions */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-[#EAE5D9]">
              <div className="flex items-center gap-2 text-xs">
                <Label className="text-[10px] uppercase text-[#8B7355]">Reassign:</Label>
                <Select value={a.branch_id || ""} onValueChange={(v) => reassign(a, v)}>
                  <SelectTrigger className="h-7 w-44 text-xs" data-testid={`fa-reassign-${a.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => toggleActive(a)} className={a.is_active === false ? "text-emerald-700" : "text-amber-700"} data-testid={`fa-toggle-${a.id}`}>
                  <Power className="w-3.5 h-3.5 mr-1" /> {a.is_active === false ? "Enable" : "Disable"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(a)} className="text-red-600" data-testid={`fa-delete-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Recent activity */}
            <button onClick={() => toggleExpanded(a.id)} className="w-full mt-3 flex items-center gap-1 text-xs text-[#5C4033] hover:text-[#C05621]" data-testid={`fa-audit-toggle-${a.id}`}>
              {expanded[a.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <Activity className="w-3 h-3" /> Recent activity (last 10)
            </button>
            {expanded[a.id] && (
              <div className="mt-2 border border-[#EAE5D9] rounded p-2 bg-[#FDFBF7]" data-testid={`fa-audit-${a.id}`}>
                {(auditRows[a.id] || []).length === 0 ? (
                  <div className="text-[11px] text-[#8B7355] text-center py-3">No recorded activity yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {(auditRows[a.id] || []).map((r) => (
                      <div key={r.id} className="text-[11px] flex items-start gap-2">
                        <span className="text-[#8B7355] whitespace-nowrap font-mono">{new Date(r.at).toLocaleString()}</span>
                        <Badge variant="outline" className="text-[10px] py-0">{r.action}</Badge>
                        {r.entity_label && <span className="text-[#5C4033] truncate">{r.entity_label}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {admins.length === 0 && !loading && (
          <div className="col-span-2 text-center py-16 bg-white border border-dashed border-[#EAE5D9] rounded-lg">
            <Users className="w-10 h-10 mx-auto text-[#8B7355] opacity-40 mb-2" />
            <h3 className="font-[Outfit] text-lg font-semibold">No Factory Admins yet</h3>
            <p className="text-sm text-[#8B7355] mt-1">Create the first one to give a factory manager scoped access to only their factory.</p>
            <Button onClick={() => setOpenCreate(true)} className="mt-3 bg-[#C05621] text-white"><Plus className="w-4 h-4 mr-1" /> Add Factory Admin</Button>
          </div>
        )}
      </div>

      {openCreate && <CreateDialog branches={branches} onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
      {openReset && <ResetPasswordDialog admin={openReset} onClose={() => setOpenReset(null)} />}
      {openPerms && <PermissionsDialog admin={openPerms} onClose={() => setOpenPerms(null)} />}
    </div>
  );
}

// ================= Create =================
function CreateDialog({ branches, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", branch_id: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password || !form.branch_id) {
      toast.error("Name, email, password and factory are required"); return;
    }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/factory-admins", form);
      toast.success("Factory Admin created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle className="font-[Outfit]">New Factory Admin</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div><Label>Full name*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fa-form-name" /></div>
          <div><Label>Email*</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fa-form-email" /></div>
          <div><Label>Initial password* (min 6 chars)</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Share this with them once — they can reset later" data-testid="fa-form-password" /></div>
          <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91-98…" /></div>
          <div><Label>Assign to factory*</Label>
            <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
              <SelectTrigger data-testid="fa-form-branch"><SelectValue placeholder="Pick a factory" /></SelectTrigger>
              <SelectContent className="bg-white">
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} {b.city ? `· ${b.city}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-[11px] bg-[#FEF9EA] border border-amber-200 p-2 rounded text-amber-900">
            <ShieldAlert className="w-3 h-3 inline mr-1" /> Factory Admins have full access <b>within their assigned factory only</b>. Head Office & other factories remain out of reach.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="fa-form-save">Create Admin</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================= Reset Password =================
function ResetPasswordDialog({ admin, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pw.length < 6) { toast.error("Min 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/factory-admins/${admin.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${admin.email}. Share the new password securely.`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle className="font-[Outfit]">Reset Password — {admin.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="text-sm text-[#5C4033]">
            Set a new password for <b>{admin.email}</b>. Their existing sessions will remain active until they log out — recommend disabling them first for critical resets.
          </div>
          <div><Label>New password*</Label>
            <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min 6 chars" data-testid="fa-reset-password-input" />
          </div>
          <div className="text-[11px] bg-red-50 border border-red-200 p-2 rounded text-red-900">
            <ShieldAlert className="w-3 h-3 inline mr-1" /> Passwords are shown in plaintext here — copy it, then rotate again after they log in.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fa-reset-submit"><KeyRound className="w-3.5 h-3.5 mr-1" /> Reset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================= Permissions Editor =================
function PermissionsDialog({ admin, onClose }) {
  const [data, setData] = useState(null);
  const [grant, setGrant] = useState({});
  const [revoke, setRevoke] = useState({});
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const r = await api.get(`/factory-admins/${admin.id}/permissions`);
        setData(r.data);
        setGrant(r.data.permissions_grant || {});
        setRevoke(r.data.permissions_revoke || {});
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
      finally { setBusy(false); }
    })();
  }, [admin.id]);

  const toggle = (which, mod, act) => {
    const src = which === "grant" ? grant : revoke;
    const cur = new Set(src[mod] || []);
    if (cur.has(act)) cur.delete(act); else cur.add(act);
    const next = { ...src, [mod]: Array.from(cur).filter(Boolean) };
    if (next[mod].length === 0) delete next[mod];
    (which === "grant" ? setGrant : setRevoke)(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/factory-admins/${admin.id}/permissions`,
        { permissions_grant: grant, permissions_revoke: revoke });
      setData(r.data);
      setGrant(r.data.permissions_grant || {});
      setRevoke(r.data.permissions_revoke || {});
      toast.success("Permissions saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const isRoleAllowed = (mod, act) => ((data?.role_permissions || {})[mod] || []).includes(act);
  const isEffective = (mod, act) => ((data?.permissions_effective || {})[mod] || []).includes(act);
  const isGranted = (mod, act) => (grant[mod] || []).includes(act);
  const isRevoked = (mod, act) => (revoke[mod] || []).includes(act);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-[Outfit]">Permissions — {admin.name}</DialogTitle></DialogHeader>
        {busy ? (
          <div className="text-center py-10 text-[#8B7355]">Loading permissions…</div>
        ) : data && (
          <div className="space-y-3 pt-2">
            <div className="text-sm text-[#5C4033] bg-[#FEF9EA] border border-amber-200 rounded p-2">
              <ShieldAlert className="w-3 h-3 inline mr-1" />
              Factory Admins already have most permissions from their role. Use <b className="text-emerald-700">Grant</b> to give extras beyond the role, and <b className="text-red-700">Revoke</b> to remove access even where the role would allow it. <b>Revoke wins.</b>
            </div>

            <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
              <table className="w-full text-sm" data-testid="fa-perms-grid">
                <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Module</th>
                    {(data.actions_catalog || []).map((a) => (
                      <th key={a} className="text-center px-2 py-2">{a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.modules_catalog || []).map((mod) => (
                    <tr key={mod} className="border-t border-[#EAE5D9]">
                      <td className="px-3 py-2 font-medium flex items-center gap-1">
                        {mod}
                        {(data.sensitive_modules || []).includes(mod) && <Badge className="border-0 bg-red-50 text-red-700 text-[9px]">sensitive</Badge>}
                      </td>
                      {(data.actions_catalog || []).map((act) => {
                        const roleAllowed = isRoleAllowed(mod, act);
                        const granted = isGranted(mod, act);
                        const revoked = isRevoked(mod, act);
                        const effective = isEffective(mod, act);
                        return (
                          <td key={act} className="px-2 py-1.5 text-center">
                            <PermCell
                              roleAllowed={roleAllowed}
                              granted={granted}
                              revoked={revoked}
                              effective={effective}
                              onGrant={() => toggle("grant", mod, act)}
                              onRevoke={() => toggle("revoke", mod, act)}
                              testid={`fa-perm-${mod}-${act}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-[11px] flex flex-wrap gap-3 text-[#5C4033]">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Role allows / effective</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200 border border-emerald-500 inline-block" /> Granted (override)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block" /> Revoked (override)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-200 inline-block" /> Denied</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving || busy} className="bg-[#2C1810] text-white" data-testid="fa-perms-save">Save permissions</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PermCell({ roleAllowed, granted, revoked, effective, onGrant, onRevoke, testid }) {
  // 3 clickable states:  role_allowed (green solid) → grant/revoke tri-state
  // Left click = grant toggle, right-click / Alt+click = revoke toggle
  const color = revoked
    ? "bg-red-500 hover:bg-red-600"
    : granted
    ? "bg-emerald-200 border-2 border-emerald-500 hover:bg-emerald-300"
    : effective
    ? "bg-emerald-500 hover:bg-emerald-600"
    : "bg-slate-200 hover:bg-slate-300";
  const symbol = revoked ? "✕" : granted || effective ? "✓" : "";
  const title = revoked
    ? "REVOKED · click to un-revoke · Alt-click to toggle grant"
    : granted
    ? "GRANTED (extra) · click to un-grant · Alt-click to revoke"
    : effective
    ? "Allowed by role · Alt-click to REVOKE"
    : "Denied · click to GRANT · Alt-click to explicit revoke";
  const handle = (e) => {
    if (e.altKey || e.button === 2) { e.preventDefault(); onRevoke(); }
    else { onGrant(); }
  };
  return (
    <button
      onClick={handle}
      onContextMenu={(e) => { e.preventDefault(); onRevoke(); }}
      title={title}
      data-testid={testid}
      className={`w-6 h-6 rounded text-white text-xs font-bold flex items-center justify-center ${color}`}
    >
      {symbol}
    </button>
  );
}
